import { Card, Row, Col, Form, Accordion, Dropdown, Button, Spinner } from "react-bootstrap";
import React from "react";
import PropTypes from "prop-types";
import * as realitioLibQuestionFormatter from "@reality.eth/reality-eth-lib/formatters/question";
import { ReactComponent as AvatarSVG } from "../assets/images/avatar.svg";
import { ReactComponent as ScalesSVG } from "../assets/images/scales.svg";
import { ReactComponent as InfoSVG } from "../assets/images/info.svg";

import DisputeTimeline from "components/disputeTimeline";
import EvidenceTimeline from "components/evidenceTimeline";
import CrowdfundingCard from "components/crowdfundingCard";
import { ethers } from "ethers";
import networkMap from "../ethereum/network-contract-mapping";

import AlertMessage from "components/alertMessage";
import RealityQuestionNotice from "components/realityQuestionNotice";

import styles from "components/styles/disputeDetails.module.css";

// Constants to avoid magic numbers
const PERCENTAGE_SCALING_FACTOR = 10000n;
const PERCENTAGE_SCALING_DIVISOR = 100;
const DISPUTE_PERIOD_APPEAL = 3;
const DISPUTE_PERIOD_EXECUTION = 4;
const BINARY_PADDING_WIDTH = 4;
const HEX_PREFIX_LENGTH = 2;

const QuestionTypes = Object.freeze({
  "single-select": "Multiple choice: single select",
  "multiple-select": "Multiple choice: multiple select",
  uint: "Non-negative number",
  int: "Number",
  string: "Text",
  datetime: "Date",
  hash: "Hash"
});

class DisputeDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeKey: 0,
    };
  }

  componentDidUpdate(previousProperties) {
    if (this.props.network !== previousProperties.network)
      window.location.reload();

    if (this.props.arbitratorDisputeID !== previousProperties.arbitratorDisputeID) {
      this.setState({ activeKey: 0 });
    }
  }

  componentDidMount() {
    // Component initialization complete
  }

  handleAccordionSelect = e => {
    this.setState({ activeKey: e });
  };

  calculateTotalCost = rulingOption => {
    // Unslashed contract violates IDisputeResolver interface by not letting option 0: refuse to rule to be funded.
    // Subsequently, in case of a ruling 0, contract considers remaining ruling options as winners, instead of losers.
    // Therefore we have to make an exception in this function for the following list of irregular contracts.

    const { currentRuling, appealCost, multipliers, exceptionalContractAddresses, arbitrated } = this.props;

    //Means it is an EscrowV1 dispute, for which there are no calculations needed
    if (networkMap[this.props.network].ESCROW_V1_CONTRACTS.includes(arbitrated)) {
      return appealCost;
    }

    let stake;

    if (currentRuling == rulingOption || (exceptionalContractAddresses.includes(arbitrated) && currentRuling == 0)) {
      stake = (appealCost * multipliers.winnerStakeMultiplier) / multipliers.denominator;
    } else {
      return this.calculateAmountRemainsToBeRaisedForLoser();
    }

    return appealCost + stake;
  };

  calculateAmountRemainsToBeRaised = rulingOption => {
    const { contributions } = this.props;
    const raisedSoFar = contributions[rulingOption] ?? 0;

    return this.calculateTotalCost(rulingOption) - BigInt(raisedSoFar);
  };

  calculateAmountRemainsToBeRaisedForLoser = () => {
    const { appealCost, multipliers } = this.props;
    const stake = (appealCost * multipliers.loserStakeMultiplier) / multipliers.denominator;

    return BigInt(appealCost) + BigInt(stake);
  };

  calculateAppealPeriod = rulingOption => {
    const { currentRuling, multipliers, appealPeriod, exceptionalContractAddresses, arbitrated } = this.props;

    if (currentRuling == rulingOption || (exceptionalContractAddresses.includes(arbitrated) && currentRuling == 0)) {
      return appealPeriod.end;
    }

    const { start, end } = appealPeriod;
    const loserMultiplier = multipliers.loserAppealPeriodMultiplier;
    const denominator = multipliers.denominator;

    return Number(start + (end - start) * loserMultiplier / denominator);
  };

  calculateLoserAppealPeriod = () => {
    const { multipliers, appealPeriod } = this.props;

    const { start, end } = appealPeriod;
    const loserMultiplier = multipliers.loserAppealPeriodMultiplier;
    const denominator = multipliers.denominator;

    return Number(start + (end - start) * loserMultiplier / denominator);
  };

  calculateFundingPercentage = (rulingOption, contributions) => {
    const totalCost = this.calculateTotalCost(rulingOption);
    const raisedSoFar = contributions[rulingOption] ?? 0;

    if (totalCost === 0) return 0;

    const scaled = (BigInt(raisedSoFar) * PERCENTAGE_SCALING_FACTOR) / totalCost;
    return Number(scaled) / PERCENTAGE_SCALING_DIVISOR;
  }

  // Helper method to validate and process hex strings
  processHexValue = hashValue => {
    const hexWithoutPrefix = hashValue.slice(HEX_PREFIX_LENGTH);

    if (hexWithoutPrefix === '') {
      throw new Error('Invalid hex value: empty hex string');
    }

    if (!/^[0-9a-fA-F]+$/.test(hexWithoutPrefix)) {
      throw new Error(`Invalid hex value: contains non-hex characters: ${hashValue}`);
    }

    const numericValue = BigInt('0x' + hexWithoutPrefix);
    return (numericValue - 1n).toString(16);
  };

  // Helper method to process numeric strings
  processNumericValue = hashValue => {
    if (hashValue.includes('e') || hashValue.includes('E')) {
      throw new Error(`Hash value precision lost during processing. Please report this issue.`);
    }

    try {
      const numericValue = BigInt(hashValue);
      return (numericValue - 1n).toString(16);
    } catch (error) {
      throw new Error(`Invalid hash value: not a valid number or hex string: ${hashValue}. ${error.message}`);
    }
  };

  // Helper method to process hash type rulings
  processHashRuling = (currentRuling, metaEvidenceJSON) => {
    const hashValue = currentRuling.toString();
    const isHexString = /^0x/i.test(hashValue);

    const finalHexValue = isHexString
      ? this.processHexValue(hashValue)
      : this.processNumericValue(hashValue);

    return realitioLibQuestionFormatter.getAnswerString(
      {
        decimals: metaEvidenceJSON.rulingOptions.precision,
        outcomes: metaEvidenceJSON.rulingOptions.titles,
        type: "hash",
      },
      realitioLibQuestionFormatter.padToBytes32(finalHexValue)
    );
  };

  // Helper method to process numeric type rulings
  processNumericRuling = (currentRuling, metaEvidenceJSON) => {
    const rulingStr = currentRuling.toString();
    let rulingValue;

    if (rulingStr.includes('e') || rulingStr.includes('E')) {
      const numValue = Number(rulingStr);
      if (!isFinite(numValue)) {
        throw new Error(`Invalid number: ${rulingStr}`);
      }
      rulingValue = BigInt(Math.floor(numValue));
    } else {
      rulingValue = BigInt(rulingStr);
    }

    return realitioLibQuestionFormatter.getAnswerString(
      {
        decimals: metaEvidenceJSON.rulingOptions.precision,
        outcomes: metaEvidenceJSON.rulingOptions.titles,
        type: metaEvidenceJSON.rulingOptions.type,
      },
      realitioLibQuestionFormatter.padToBytes32((rulingValue - 1n).toString(16))
    );
  };

  // Helper to find Reality.eth outcomes from various possible locations
  findRealityOutcomes = (metaEvidenceJSON) => {
    const possiblePaths = [
      metaEvidenceJSON?.dispute?.template?.outcomes,
      metaEvidenceJSON?.template?.outcomes,
      metaEvidenceJSON?.outcomes,
      metaEvidenceJSON?.question?.outcomes
    ];
    return possiblePaths.find(outcomes => outcomes && Array.isArray(outcomes) && outcomes.length > 0);
  };

  convertToRealitioFormat = (currentRuling, metaEvidenceJSON) => {
    try {
      if (!metaEvidenceJSON?.rulingOptions) {
        const realityOutcomes = this.findRealityOutcomes(metaEvidenceJSON);
        if (realityOutcomes) {
          const rulingIndex = parseInt(currentRuling, 10) - 1;
          if (rulingIndex >= 0 && rulingIndex < realityOutcomes.length) {
            return realityOutcomes[rulingIndex];
          }
        }
        return currentRuling.toString();
      }

      const questionType = metaEvidenceJSON.rulingOptions.type;

      // For hash type rulings, preserve precision by handling as hex strings
      // Apply Reality.eth -1 offset consistently for both hex and numeric inputs
      if (questionType === "hash") {
        return this.processHashRuling(currentRuling, metaEvidenceJSON);
      }

      // For numeric types, apply the original logic
      return this.processNumericRuling(currentRuling, metaEvidenceJSON);
    } catch (error) {
      console.error('Error converting ruling to Realitio format:', error, 'currentRuling:', currentRuling);
      // Fallback: return a default string or the original value
      return currentRuling.toString();
    }
  };

  getWinner = (rulingFunded, currentRuling) => {
    if (rulingFunded && rulingFunded.length == 1) return rulingFunded[0];
    else return currentRuling;
  };

  // Helper method to render decision alert messages
  renderDecisionAlerts = (disputePeriod, currentRuling, metaevidenceJSON, rulingFunded, incompatible) => {
    const decisionInfoBoxContent = `This decision can be appealed within appeal period. ${incompatible ? "Go to arbitrable application to appeal this ruling." : ""}`;

    const formatRulingForDisplay = ruling => {
      if (ruling == 0) return "invalid / refused to arbitrate / tied";

      if (metaevidenceJSON.rulingOptions?.type === "hash") {
        // For hash type, display the raw hex value without Reality.eth conversion
        return `0x${BigInt(ruling).toString(16).padStart(64, '0')}`;
      } else {
        return this.convertToRealitioFormat(ruling, metaevidenceJSON);
      }
    };

    if (disputePeriod == DISPUTE_PERIOD_APPEAL) {
      return (
        <AlertMessage
          type="info"
          title={`Jury decision: ${formatRulingForDisplay(currentRuling)}`}
          content={decisionInfoBoxContent}
        />
      );
    }

    if (disputePeriod == DISPUTE_PERIOD_EXECUTION) {
      return (
        <AlertMessage
          type="info"
          title={`Winner: ${formatRulingForDisplay(this.getWinner(rulingFunded, currentRuling))}`}
          content={`${rulingFunded && rulingFunded.length == 1 ? "Won by default" : "Won by jury decision"}`}
        />
      );
    }

    return null;
  };

  // Helper method to render dispute info section
  renderDisputeInfo = (arbitratorDisputeID, arbitratorDisputeDetails, arbitratorDispute, subcourtDetails) => (
    <Row>
      <Col xl={6} md="auto" sm={true} xs={24}>
        <Form.Group>
          <Form.Label htmlFor="category">Dispute</Form.Label>
          <Form.Control id="category" as="span" title="" className="mr-4">
            <i className="purple-primary">#</i> {arbitratorDisputeID}
          </Form.Control>
        </Form.Group>
      </Col>
      <Col xl={6} md="auto" sm={true} xs={24}>
        <Form.Group className="">
          <Form.Label htmlFor="initialNumberOfJurors">Number of Votes</Form.Label>
          <Form.Control className={`mr-4 ${styles.spanWithSvgInside}`} id="initialNumberOfJurors" as="span">
            <AvatarSVG />
            <span>{parseInt(arbitratorDisputeDetails.votesLengths[0], 10)}</span>
          </Form.Control>
        </Form.Group>
      </Col>
      <Col md={true} sm={24}>
        <Form.Group>
          <Form.Label htmlFor="court">Court</Form.Label>
          <Form.Control className={styles.spanWithSvgInside} id="court" as="span">
            <ScalesSVG className={styles.scales} />
            <span>{subcourtDetails[arbitratorDispute.subcourtID]?.name}</span>
          </Form.Control>
        </Form.Group>
      </Col>
    </Row>
  );

  // Helper method to render appeal section
  renderAppealSection = (disputePeriod, totalWithdrawable, metaevidenceJSON, currentRuling, contributions, appealCallback, exceptionalContractAddresses, arbitrated, multipliers) => (
    <Card.Body>
      <div className="h1">{disputePeriod == DISPUTE_PERIOD_APPEAL ? "Appeal the decision" : "Withdraw crowdfunding rewards and refunds"}</div>
      <p className="label">
        {disputePeriod == DISPUTE_PERIOD_APPEAL
          && "In order to appeal the decision, you need to fully fund the crowdfunding deposit. The dispute will be sent" +
          " to the jurors when the full deposit is reached. Note that if the previous round loser funds its side, the previous round winner should also fully fund its side in order not to lose the case."
        }
        {disputePeriod == DISPUTE_PERIOD_EXECUTION && parseInt(totalWithdrawable, 10) != 0 ? "If you have contributed to a ruling option and in the end that ruling option was the winner you are eligible for some reward. Also, if you have contributed but appeal did not happen your contribution is refunded."
          : "You don't have any amount to withdraw. Reason might be that you did not contribute, the ruling option you have contributed did not win, you already withdrew or the ruling is not executed yet by the arbitrator."}
      </p>
      {disputePeriod == DISPUTE_PERIOD_EXECUTION && parseInt(totalWithdrawable, 10) > 0 && (
        <Row className="mt-5">
          <Col className="text-right">
            <Button className="ml-auto" onClick={this.props.withdrawCallback}>
              {`Withdraw ${ethers.formatEther(totalWithdrawable)} ETH`}
            </Button>
          </Col>
        </Row>
      )}

      {disputePeriod == DISPUTE_PERIOD_APPEAL && (
        (multipliers && this.props.appealCost != null && this.props.appealPeriod != null) ? (
          <Row className="mt-3">
            {this.renderCrowdfundingCards(metaevidenceJSON, currentRuling, contributions, appealCallback, exceptionalContractAddresses, arbitrated)}
            {this.renderVariableTypeCrowdfundingCards(metaevidenceJSON, currentRuling, contributions, appealCallback)}
          </Row>
        ) : (
          <AlertMessage
            type="warning"
            title="Appeal fees could not be calculated"
            content="This arbitrable contract uses an unsupported appeal interface, so the appeal funding amounts and deadlines can't be shown. Please use the arbitrable application to appeal."
          />
        )
      )}
    </Card.Body>
  );

  renderEscrowV1AppealSection = (disputePeriod, appealCallback, appealCost) => (
    <Card.Body>
      <div className="h1">{disputePeriod == DISPUTE_PERIOD_APPEAL ? "Appeal the decision" : "Appeal period ended"}</div>
      {disputePeriod == DISPUTE_PERIOD_APPEAL && <p className="label">In order to appeal the decision, you need to pay the appeal cost.</p>}
      {disputePeriod == DISPUTE_PERIOD_APPEAL && (
        <Button
          onClick={() =>
            appealCallback(0, ethers.formatEther(appealCost))
          }>
          Appeal - {ethers.formatEther(appealCost)} ETH
        </Button>
      )}
    </Card.Body>
  );

  // Helper method to render question section
  renderQuestionSection = (metaevidenceJSON, arbitratorDisputeID, network) => {
    const courtURL = new URL(`https://court.kleros.io/cases/${encodeURIComponent(arbitratorDisputeID)}`);
    courtURL.searchParams.set("requiredChainId", network ?? "1");

    return (
      <Card.Body className={styles.question}>
        <p>{QuestionTypes[metaevidenceJSON.rulingOptions?.type]}</p>
        <p>{metaevidenceJSON.question}</p>
        {(metaevidenceJSON.rulingOptions?.type == "single-select" || metaevidenceJSON.rulingOptions?.type == "multiple-select") && (
          <>
            <Dropdown>
              <Dropdown.Toggle block className={styles.dropdownToggle}>
                <span className="font-weight-normal">View Voting Options</span>
              </Dropdown.Toggle>

              <Dropdown.Menu dir="">
                <Dropdown.Item key={0} disabled>Option 0 - Refuse to Arbitrate / Invalid</Dropdown.Item>
                {metaevidenceJSON.rulingOptions?.titles?.map((title, index) => (
                  <Dropdown.Item key={`option-${index + 1}`} disabled>{`Option ${index + 1} - ${title}${metaevidenceJSON.rulingOptions.descriptions?.[index] != undefined ? ":" : ""
                    } ${metaevidenceJSON.rulingOptions.descriptions?.[index] != undefined
                      ? metaevidenceJSON.rulingOptions.descriptions[index]
                      : ""
                    }`}</Dropdown.Item>
                ))}
                {metaevidenceJSON.rulingOptions?.reserved &&
                  Object.entries(metaevidenceJSON.rulingOptions.reserved).map(([rulingCode, title]) => {
                    const displayCode = rulingCode.length > 12 ? `${rulingCode.slice(0, 6)}...${rulingCode.slice(-6)}` : rulingCode;
                    return (
                      <Dropdown.Item key={rulingCode} disabled>{`Option ${displayCode} - ${title}${metaevidenceJSON.rulingOptions.descriptions?.[rulingCode] != undefined ? ":" : ""
                        } ${metaevidenceJSON.rulingOptions.descriptions?.[rulingCode] != undefined
                          ? metaevidenceJSON.rulingOptions.descriptions[rulingCode]
                          : ""
                        }`}</Dropdown.Item>
                    );
                  })}

              </Dropdown.Menu>
            </Dropdown>
            <p className={styles.questionInfo}>
              <InfoSVG />
              Note that you can only view the voting options. Selected jurors can vote using{" "}
              <a href={courtURL.toString()} target="_blank" rel="noreferrer noopener">
                Court
              </a>
              .
            </p>
          </>
        )}
      </Card.Body>
    );
  };

  // Helper method to render evidence section
  renderEvidenceSection = ({ incompatible, metaevidenceJSON, evidences, disputeEvent, disputePeriod, publishCallback, submitEvidenceCallback, isAuthenticated, isSigningIn, onSignIn }) => (
    <Card.Body>
      <EvidenceTimeline
        evidenceSubmissionEnabled={!incompatible}
        metaevidence={metaevidenceJSON}
        evidences={evidences}
        dispute={disputeEvent}
        disputePeriod={disputePeriod}
        publishCallback={publishCallback}
        submitEvidenceCallback={submitEvidenceCallback}
        appealDecisions={this.state.appealDecisions}
        isAuthenticated={isAuthenticated}
        isSigningIn={isSigningIn}
        onSignIn={onSignIn}
      />
    </Card.Body>
  );

  // Helper method to check if required data is available
  hasRequiredData = (metaevidenceJSON, arbitratorDispute, subcourts, subcourtDetails, arbitratorDisputeDetails) => {
    return metaevidenceJSON && arbitratorDispute && subcourts?.length > 0 &&
      subcourtDetails?.length > 0 && arbitratorDisputeDetails;
  };

  // Helper method to render appeal card conditionally
  renderAppealCard = (arbitratorDispute, disputePeriod, contributions, multipliers, appealCost, appealPeriod, arbitrated, totalWithdrawable, metaevidenceJSON, currentRuling, appealCallback, exceptionalContractAddresses, activeKey, isEscrowV1Dispute, loading) => {
    if (!arbitratorDispute || disputePeriod < DISPUTE_PERIOD_APPEAL || !arbitrated) {
      return null;
    }

    let body;
    if (loading) {
      body = (
        <Card.Body className="text-center">
          <Spinner animation="border" role="status" />
        </Card.Body>
      );
    } else if (isEscrowV1Dispute) {
      body = this.renderEscrowV1AppealSection(disputePeriod, appealCallback, appealCost);
    } else {
      body = this.renderAppealSection(disputePeriod, totalWithdrawable, metaevidenceJSON, currentRuling, contributions, appealCallback, exceptionalContractAddresses, arbitrated, multipliers);
    }

    return (
      <Card>
        <Accordion.Toggle className={activeKey == 1 ? "open" : "closed"} as={Card.Header} eventKey="1">
          Appeal
        </Accordion.Toggle>
        <Accordion.Collapse eventKey="1">
          {body}
        </Accordion.Collapse>
      </Card>
    );
  };

  // Helper method to render crowdfunding cards for different question types
  renderCrowdfundingCards = (metaevidenceJSON, currentRuling, contributions, appealCallback, exceptionalContractAddresses, arbitrated) => {
    const cards = [];

    // Invalid/Refused option
    if (!exceptionalContractAddresses.includes(arbitrated)) {
      cards.push(
        <Col key={0} className="pb-4" xl={8} lg={12} xs={24}>
          <CrowdfundingCard
            key={0}
            title={"Invalid / Refused to Arbitrate / Tied"}
            winner={currentRuling == 0}
            fundingPercentage={this.calculateFundingPercentage(0, contributions).toFixed(2)}
            appealPeriodEnd={this.calculateAppealPeriod(0)}
            suggestedContribution={ethers.formatEther(this.calculateAmountRemainsToBeRaised(0))}
            appealCallback={appealCallback}
            rulingOptionCode={0}
          />
        </Col>
      );
    }

    // Reserved options
    if (metaevidenceJSON.rulingOptions?.reserved) {
      Object.entries(metaevidenceJSON.rulingOptions.reserved).forEach(([rulingCode, title]) => {
        const hexToNumberString = hex => ethers.getBigInt(hex).toString();
        cards.push(
          <Col key={hexToNumberString(rulingCode)} className="pb-4" xl={8} lg={12} xs={24}>
            <CrowdfundingCard
              key={hexToNumberString(rulingCode)}
              title={title}
              winner={currentRuling == hexToNumberString(rulingCode)}
              fundingPercentage={this.calculateFundingPercentage(hexToNumberString(rulingCode), contributions).toFixed(2)}
              appealPeriodEnd={this.calculateAppealPeriod(hexToNumberString(rulingCode))}
              suggestedContribution={ethers.formatEther(this.calculateAmountRemainsToBeRaised(hexToNumberString(rulingCode)))}
              appealCallback={appealCallback}
              rulingOptionCode={hexToNumberString(rulingCode)}
            />
          </Col>
        );
      });
    }

    // Type-specific cards
    const questionType = metaevidenceJSON.rulingOptions?.type;

    if (questionType === "single-select" && metaevidenceJSON.rulingOptions?.titles) {
      metaevidenceJSON.rulingOptions.titles.forEach((title, index) => {
        cards.push(
          <Col key={index + 1} className="pb-4" xl={8} lg={12} xs={24}>
            <CrowdfundingCard
              title={title}
              winner={currentRuling == index + 1}
              fundingPercentage={this.calculateFundingPercentage(index + 1, contributions).toFixed(2)}
              appealPeriodEnd={this.calculateAppealPeriod(index + 1)}
              suggestedContribution={ethers.formatEther(this.calculateAmountRemainsToBeRaised(index + 1))}
              appealCallback={appealCallback}
              rulingOptionCode={index + 1}
            />
          </Col>
        );
      });
    } else if (questionType === "multiple-select" && metaevidenceJSON.rulingOptions?.titles) {
      Array.from(Array(2 ** metaevidenceJSON.rulingOptions.titles.length).keys()).forEach(comboValue => {
        const title = comboValue == 0
          ? "None"
          : comboValue
            .toString(2)
            .padStart(BINARY_PADDING_WIDTH, "0")
            .split("")
            .reverse()
            .map((bit, i) => (bit === "1" ? metaevidenceJSON.rulingOptions.titles[i] : null))
            .join(" ");

        cards.push(
          <Col key={`combo-${comboValue + 1}`} className="pb-4" xl={8} lg={12} xs={24}>
            <CrowdfundingCard
              title={title}
              winner={currentRuling == comboValue + 1}
              fundingPercentage={this.calculateFundingPercentage(comboValue + 1, contributions).toFixed(2)}
              appealPeriodEnd={this.calculateAppealPeriod(comboValue + 1)}
              suggestedContribution={ethers.formatEther(this.calculateAmountRemainsToBeRaised(comboValue + 1))}
              appealCallback={appealCallback}
              rulingOptionCode={comboValue + 1}
            />
          </Col>
        );
      });
    } else {
      // Try to use Reality.eth parsed outcomes if available
      const realityOutcomes = this.findRealityOutcomes(metaevidenceJSON);
      const fallbackTitles = realityOutcomes || ["Yes", "No"]; // Basic fallback for Reality.eth questions

      fallbackTitles.forEach((title, index) => {
        cards.push(
          <Col key={`fallback-${index + 1}`} className="pb-4" xl={8} lg={12} xs={24}>
            <CrowdfundingCard
              title={title}
              winner={currentRuling == index + 1}
              fundingPercentage={this.calculateFundingPercentage(index + 1, contributions).toFixed(2)}
              appealPeriodEnd={this.calculateAppealPeriod(index + 1)}
              suggestedContribution={ethers.formatEther(this.calculateAmountRemainsToBeRaised(index + 1))}
              appealCallback={appealCallback}
              rulingOptionCode={index + 1}
            />
          </Col>
        );
      });
    }

    return cards;
  };

  // Helper method to render variable type crowdfunding cards
  renderVariableTypeCrowdfundingCards = (metaevidenceJSON, currentRuling, contributions, appealCallback) => {
    const questionType = metaevidenceJSON.rulingOptions?.type;
    const isVariableType = ["uint", "int", "string", "datetime", "hash"].includes(questionType);

    if (!isVariableType) return null;

    const cards = [];

    // Note: For hash type questions, we don't apply Reality.eth conversion (subtracting 1)
    // when displaying contribution keys because they already represent the actual ruling values
    // that were appealed for. This fixes the off-by-one display issue.

    // Other contributions (not current ruling)
    Object.keys(contributions)
      .filter(key => key !== this.props.currentRuling.toString())
      .forEach(key => {
        let title;
        if (questionType === "string") {
          title = ethers.toUtf8String(ethers.hexlify(key));
        } else if (questionType === "hash") {
          // For hash type, display the raw hex value without Reality.eth conversion
          title = `0x${BigInt(key).toString(16).padStart(64, '0')}`;
        } else {
          title = this.convertToRealitioFormat(key, metaevidenceJSON);
        }

        cards.push(
          <Col key={key} className="pb-4" xl={8} lg={12} xs={24}>
            <CrowdfundingCard
              title={title}
              rulingOptionCode={key.toString()}
              winner={currentRuling == key}
              fundingPercentage={this.calculateFundingPercentage(key, contributions).toFixed(2)}
              suggestedContribution={ethers.formatEther(this.calculateAmountRemainsToBeRaised(key))}
              appealPeriodEnd={this.calculateAppealPeriod(key)}
              appealCallback={appealCallback}
              metaevidenceJSON={metaevidenceJSON}
            />
          </Col>
        );
      });

    // Current ruling card (if not 0)
    if (this.props.currentRuling != 0) {
      let currentRulingTitle;
      if (questionType === "hash") {
        // For hash type, display the raw hex value without Reality.eth conversion
        currentRulingTitle = `0x${BigInt(currentRuling).toString(16).padStart(64, '0')}`;
      } else {
        currentRulingTitle = this.convertToRealitioFormat(currentRuling, metaevidenceJSON);
      }

      cards.push(
        <Col key="current-ruling" className="pb-4" xl={8} lg={12} xs={24}>
          <CrowdfundingCard
            title={currentRulingTitle}
            rulingOptionCode={currentRuling.toString()}
            winner={true}
            fundingPercentage={this.calculateFundingPercentage(currentRuling, contributions).toFixed(2)}
            appealPeriodEnd={this.calculateAppealPeriod(currentRuling)}
            suggestedContribution={ethers.formatEther(this.calculateAmountRemainsToBeRaisedForLoser())}
            appealCallback={appealCallback}
            metaevidenceJSON={metaevidenceJSON}
          />
        </Col>
      );
    }

    // Variable input card
    cards.push(
      <Col key="variable-input" className="pb-4" xl={8} lg={12} xs={24}>
        <CrowdfundingCard
          variable={questionType}
          winner={false}
          fundingPercentage={0}
          appealPeriodEnd={this.calculateLoserAppealPeriod()}
          suggestedContribution={ethers.formatEther(this.calculateAmountRemainsToBeRaisedForLoser())}
          appealCallback={appealCallback}
          metaevidenceJSON={metaevidenceJSON}
        />
      </Col>
    );

    return cards;
  };

  render() {
    const {
      arbitrated,
      network,
      metaevidenceJSON,
      evidences,
      arbitratorDisputeID,
      arbitratorDispute,
      incompatible,
      subcourts,
      subcourtDetails,
      arbitratorDisputeDetails,
      currentRuling,
      disputeEvent,
      publishCallback,
      submitEvidenceCallback,
      appealCost,
      appealPeriod,
      appealCallback,
      contributions,
      rulingFunded,
      multipliers,
      totalWithdrawable,
      exceptionalContractAddresses,
      isAuthenticated,
      isSigningIn,
      onSignIn,
      loading
    } = this.props;

    const { activeKey } = this.state;

    // Early return if required data is not available
    if (!this.hasRequiredData(metaevidenceJSON, arbitratorDispute, subcourts, subcourtDetails, arbitratorDisputeDetails)) {
      return <div></div>;
    }

    const disputePeriod = parseInt(arbitratorDispute.period, 10);
    const isEscrowV1Dispute = networkMap[network].ESCROW_V1_CONTRACTS.includes(arbitrated);

    return (
      <section className={styles.disputeDetails}>
        <DisputeTimeline
          period={disputePeriod}
          lastPeriodChange={arbitratorDispute.lastPeriodChange.toString()}
          timesPerPeriod={subcourts[arbitratorDispute.subcourtID.toString()]?.[1] || ["0", "0", "0", "0"]}
        />
        <hr className="mt-4" />

        {this.renderDisputeInfo(arbitratorDisputeID, arbitratorDisputeDetails, arbitratorDispute, subcourtDetails)}

        {this.renderDecisionAlerts(disputePeriod, currentRuling, metaevidenceJSON, rulingFunded, incompatible)}

        <RealityQuestionNotice realityQuestion={metaevidenceJSON?.realityQuestion} />

        <Accordion
          className={`mt-4 ${styles.accordion}`}
          onSelect={this.handleAccordionSelect}
        >
          {this.renderAppealCard(arbitratorDispute, disputePeriod, contributions, multipliers, appealCost, appealPeriod, arbitrated, totalWithdrawable, metaevidenceJSON, currentRuling, appealCallback, exceptionalContractAddresses, activeKey, isEscrowV1Dispute, loading)}

          <Card>
            <Accordion.Toggle className={activeKey == 2 ? "open" : "closed"} as={Card.Header} eventKey="2">
              Question
            </Accordion.Toggle>
            <Accordion.Collapse eventKey="2">
              {this.renderQuestionSection(metaevidenceJSON, arbitratorDisputeID, network)}
            </Accordion.Collapse>
          </Card>

          <Card>
            <Accordion.Toggle className={activeKey == 3 ? "open" : "closed"} as={Card.Header} eventKey="3">
              Evidence
            </Accordion.Toggle>
            <Accordion.Collapse eventKey="3">
              {this.renderEvidenceSection({ incompatible, metaevidenceJSON, evidences, disputeEvent, disputePeriod, publishCallback, submitEvidenceCallback, isAuthenticated, isSigningIn, onSignIn })}
            </Accordion.Collapse>
          </Card>
        </Accordion>
      </section>
    );
  }
}

DisputeDetails.propTypes = {
  exceptionalContractAddresses: PropTypes.array,
  isAuthenticated: PropTypes.bool.isRequired,
  isSigningIn: PropTypes.bool.isRequired,
  onSignIn: PropTypes.func.isRequired,
};

export default DisputeDetails;
